import React, { type CSSProperties, type PropsWithChildren } from "react";
import MuiDialog, {
  type DialogProps as MuiDialogProps,
  type DialogOwnerState,
} from "@mui/material/Dialog";
import type { SxProps, Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MELA_PHONE_QUERY } from "../theme/breakpoints";
import { MELA_CHAT_LAUNCHER_CLEARANCE_VAR } from "./layout";

export type MelaModalSize = "xs" | "sm" | "md" | "lg" | "workspace";

type MelaModalSizePreset = {
  fullWidth?: boolean;
  maxWidth?: MuiDialogProps["maxWidth"];
  modalContainerStyle?: CSSProperties;
};

const MODAL_SIZE_PRESETS: Record<MelaModalSize, MelaModalSizePreset> = {
  xs: {
    fullWidth: true,
    maxWidth: "xs",
  },
  sm: {
    fullWidth: true,
    maxWidth: "sm",
  },
  md: {
    fullWidth: true,
    maxWidth: "md",
  },
  lg: {
    fullWidth: true,
    maxWidth: "lg",
  },
  workspace: {
    fullWidth: true,
    maxWidth: false,
    modalContainerStyle: {
      maxWidth: "95vw",
      width: "100%",
      maxHeight: "95vh",
      height: "100%",
      padding: 0,
      overflow: "hidden",
    },
  },
};

export interface MelaModalProps
  extends Omit<
    MuiDialogProps,
    | "open"
    | "onClose"
    | "children"
    | "PaperProps"
    | "BackdropProps"
    | "disableEnforceFocus"
  > {
  show?: boolean;
  open?: boolean;
  size?: MelaModalSize;
  backdrop?: boolean;
  onClose?: (...args: any[]) => void;
  modalContainerStyle?: CSSProperties;
  backdropStyle?: CSSProperties;
  className?: string;
  paperSx?: SxProps<Theme>;
  railAware?: boolean;
  fullScreenOnPhone?: boolean;
}

// Transparent borders, not padding: the inset adds to the caller's padding and
// clips scrolled content. The bottom one clears the compact chat launcher.
const PHONE_FULL_SCREEN_PAPER_SX: SxProps<Theme> = {
  boxSizing: "border-box",
  borderStyle: "solid",
  borderColor: "transparent",
  borderWidth: `env(safe-area-inset-top) env(safe-area-inset-right) calc(env(safe-area-inset-bottom) + var(${MELA_CHAT_LAUNCHER_CLEARANCE_VAR}, 0px)) env(safe-area-inset-left)`,
};

const joinClassNames = (...classNames: Array<string | undefined>) =>
  classNames.filter(Boolean).join(" ");

const mergeSx = (...values: Array<SxProps<Theme> | undefined>) => {
  const filteredValues = values.filter(
    (value): value is SxProps<Theme> => value !== undefined,
  );

  if (filteredValues.length === 0) {
    return undefined;
  }

  if (filteredValues.length === 1) {
    return filteredValues[0];
  }

  return filteredValues as SxProps<Theme>;
};

type SlotProp<TProps> = TProps | ((ownerState: DialogOwnerState) => TProps);

const mergeSlotProp = <TProps extends Record<string, any>>(
  slotProp: SlotProp<TProps> | undefined,
  getAdditions: (resolvedSlotProp?: TProps) => TProps,
): SlotProp<TProps> => {
  if (typeof slotProp === "function") {
    return (ownerState: DialogOwnerState) => {
      const resolvedSlotProp = slotProp(ownerState);

      return {
        ...resolvedSlotProp,
        ...getAdditions(resolvedSlotProp),
      };
    };
  }

  return {
    ...(slotProp || {}),
    ...getAdditions(slotProp),
  };
};

export const MelaModal = ({
  show,
  open,
  size,
  backdrop = true,
  onClose,
  fullScreen,
  fullWidth,
  maxWidth,
  disableRestoreFocus,
  hideBackdrop,
  modalContainerStyle = {},
  backdropStyle = {},
  className,
  paperSx,
  railAware = true,
  fullScreenOnPhone = false,
  slotProps,
  sx,
  children,
  ...dialogProps
}: PropsWithChildren<MelaModalProps>) => {
  const isOpen = open ?? show ?? false;
  const isPhone = useMediaQuery(MELA_PHONE_QUERY);
  const isPhoneFullScreen = fullScreenOnPhone && isPhone;
  const resolvedFullScreen = isPhoneFullScreen || fullScreen;
  const sizePreset = resolvedFullScreen
    ? undefined
    : size
      ? MODAL_SIZE_PRESETS[size]
      : undefined;
  const resolvedModalContainerStyle = {
    ...(sizePreset?.modalContainerStyle || {}),
    ...modalContainerStyle,
  };
  return (
    <MuiDialog
      {...dialogProps}
      open={Boolean(isOpen)}
      fullScreen={resolvedFullScreen}
      fullWidth={fullWidth ?? sizePreset?.fullWidth}
      maxWidth={maxWidth ?? sizePreset?.maxWidth}
      hideBackdrop={hideBackdrop ?? false}
      // Keep page-level surfaces such as the unique chat usable while a dialog is open.
      disableEnforceFocus
      disableRestoreFocus={disableRestoreFocus ?? false}
      onClose={(event, reason) => {
        // `backdrop` controls backdrop-click dismissal only. Use `hideBackdrop` for visibility.
        if (!backdrop && reason === "backdropClick") {
          return;
        }

        onClose?.(event, reason);
      }}
      sx={mergeSx(
        !railAware
          ? {
              "& .MuiDialog-container": {
                paddingRight: 0,
              },
            }
          : undefined,
        sx,
      )}
      slotProps={{
        ...slotProps,
        paper: mergeSlotProp(slotProps?.paper, (resolvedPaperProps) => ({
          className: joinClassNames(className, resolvedPaperProps?.className),
          style: {
            ...resolvedModalContainerStyle,
            ...(resolvedPaperProps?.style || {}),
          },
          sx: mergeSx(
            paperSx,
            resolvedPaperProps?.sx,
            isPhoneFullScreen ? PHONE_FULL_SCREEN_PAPER_SX : undefined,
          ),
        })),
        backdrop: mergeSlotProp(
          slotProps?.backdrop,
          (resolvedBackdropProps) => ({
            style: {
              ...backdropStyle,
              ...(resolvedBackdropProps?.style || {}),
            },
          }),
        ),
      }}
    >
      {children}
    </MuiDialog>
  );
};
